import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal, Image, Spinner, Button, Form, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useState } from "react";
import { faXTwitter, faFacebook } from "@fortawesome/free-brands-svg-icons";
import { faCode, faEnvelope, faClipboard, faQrcode } from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import { Formik } from "formik";
import { APIShareEmail } from "../../helper/APIFunctions.js";
import "./ShareModal.css";
import {ANATOMY_EXPLORER_LANGUAGE_MAP } from "../../helper/Language_Utils";

const emailShareSchema = Yup.object().shape({
  email: Yup.string().required("Required").email("Invalid Format"),
});

const ShareModal = (props) => {
  const [showState, setShowState] = useState({});
  const [shareEmailErr, setShareEmailErr] = useState("");
  const [copyTooltipText, setCopyTooltipText] = useState("Copy to clipboard");
  const [embedTooltipText, setEmbedTooltipText] = useState("Copy embed code");
  const [sharedLanguage, setSharedLanguage] = useState(null);
  const getShareUrl = () => {
    if (props.subtype === "anatomy_scene" && sharedLanguage) {
      return `${props.url}?l=${sharedLanguage}`;
    }
    return props.url;
  }
  const handleCopy = () => {
    navigator.clipboard.writeText(getShareUrl());
    setCopyTooltipText("Copied!");
    setTimeout(() => setCopyTooltipText("Copy to clipboard"), 2000);
  };

  const handleEmbedCopy = () => {
    const embedCode = `<iframe src="${getShareUrl()}" width="640" height="360"></iframe>`;
    navigator.clipboard.writeText(embedCode);
    setEmbedTooltipText("Copied!");
    setTimeout(() => setEmbedTooltipText("Copy embed code"), 2000);
  }

  return (
    <Modal show={props.show} onHide={props.onHide} centered className="share-modal">
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title as="h3">Share This {!props.isPresentation ? "Visual" : "Presentation"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center">
        <div className="share-icons-container">
            <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out this ${!props.isPresentation ? "visual" : "presentation"} from snorkle.io`)}&url=${encodeURIComponent(getShareUrl())}`} className="share-icon" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faXTwitter} title="Share to X/Twitter" />
                <span className="share-icon-label">X / Twitter</span>
            </a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}&src=sdkpreparse`} className="share-icon" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faFacebook} title="Share to Facebook" />
                <span className="share-icon-label">Facebook</span>
        </a>
            <OverlayTrigger placement="top" overlay={<Tooltip>{copyTooltipText}</Tooltip>}>
                <div className="share-icon" onClick={handleCopy}>
                    <FontAwesomeIcon icon={faClipboard} title="Copy link" />
                    <span className="share-icon-label">Copy Link</span>
                </div>
            </OverlayTrigger>
            {props.canEmail && (
                <div className="share-icon" onClick={() => setShowState({ email: !showState.email })}>
                    <FontAwesomeIcon icon={faEnvelope} title="Share via Email" />
                    <span className="share-icon-label">Email</span>
                </div>
            )}
            <div className="share-icon" onClick={() => setShowState({ embedCode: !showState.embedCode })}>
                <FontAwesomeIcon icon={faCode} title="Embed in your website" />
                <span className="share-icon-label">Embed</span>
            </div>
            {props.qr && (
              <div className="share-icon" onClick={() => setShowState({ qr: !showState.qr })}>
                  <FontAwesomeIcon icon={faQrcode} title="Show QR Code" />
                  <span className="share-icon-label">QR Code</span>
              </div>
            )}
        </div>
        {props.subtype === "anatomy_scene" && (
          <div className="share-section">
            <h5>Shared Language</h5>
            <div className="share-language-container">
              <Form.Select onChange={(e) => setSharedLanguage(e.target.value)} disabled={showState.qr} value={showState.qr ? "" :sharedLanguage} className="inline">
                <option value="">User's Browser Language</option>
                {Object.entries(ANATOMY_EXPLORER_LANGUAGE_MAP).sort((a, b) => a[1].localeCompare(b[1])).map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </Form.Select>
            </div>
          </div>
        )}
        {showState.embedCode && (
          <div className="share-section">
            <h5>Embed Code</h5>
            <div className="embed-code-container">
              <code>{`<iframe src="${getShareUrl()}" width="640" height="360"></iframe>`}</code>
                <OverlayTrigger placement="top" overlay={<Tooltip>{embedTooltipText}</Tooltip>}>
                    <Button variant="outline-light" size="sm" className="copy-embed-button" onClick={handleEmbedCopy}>
                        Copy
                    </Button>
                </OverlayTrigger>
            </div>
          </div>
        )}
        {showState.qr && 
            <div className="share-section">
                <Image src={props.qr} className="w-75 m-2" />
            </div>
        }
        {props.canEmail && showState.email && (
          <div className="share-section">
            <div className="text-danger">{shareEmailErr || ""}</div>
            <Formik
              initialValues={{ email: "" }}
              validationSchema={emailShareSchema}
              onSubmit={(values, actions) => {
                setShareEmailErr("");
                APIShareEmail(
                  props.id,
                  !!props.isPresentation,
                  values.email,
                  () => {
                    props.onHide();
                    actions.setSubmitting(false);
                  },
                  (err) => {
                    setShareEmailErr(err);
                    actions.setSubmitting(false);
                  }
                );
              }}
            >
              {({
                handleSubmit,
                handleChange,
                handleBlur,
                values,
                touched,
                errors,
                isSubmitting,
              }) => (
                <Form noValidate onSubmit={handleSubmit} className="d-flex gap-2">
                  <Form.Group className="flex-grow-1">
                    <Form.Control
                      value={values.email}
                      onChange={handleChange}
                      name="email"
                      onBlur={handleBlur}
                      isInvalid={touched.email && errors.email}
                      placeholder="Destination Email"
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Button type="submit" disabled={isSubmitting} variant="primary">
                    {isSubmitting ? (
                      <Spinner as="span" animation="border" size="sm" />
                    ) : (
                      "Send"
                    )}
                  </Button>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ShareModal;









